// modules
import { findIndex, get } from 'lodash'
import { createSelector } from 'reselect'

import {
  patientPrimaryPhone$,
  patientUuidSelector
} from 'src/store/modules/patient/profile'
// utils
import { patientDemographicsSelector } from 'src/store/modules/patient/selectors'

// constants
import { OTHER_DROPDOWN_VALUE } from 'src/constants'
import { dashToString } from 'src/utils/String'

const getPhoneLabel = (number = '', type = '') => {
  const dashedNumber = dashToString(number)

  if (typeof type === 'string') return `${type} ${dashedNumber}`

  if ('refValue' in type) return `${type.refValue} ${dashedNumber}`

  return dashedNumber
}

export const patientPhones$ = createSelector(
  patientDemographicsSelector,
  patientPrimaryPhone$,
  (demographics = {}, primaryPhone = '') =>
    get(demographics, ['phones'], []).map((item) => ({
      ...item,
      isPrimary: item.number === primaryPhone
    }))
)

export const patientPhonesDropdownWithOther$ = createSelector(
  patientDemographicsSelector,
  patientPrimaryPhone$,
  (demographicsPhones = [], primaryPhone = '') =>
    [
      ...demographicsPhones?.phones?.map(({ uuid, number, type }) => ({
        key: uuid,
        label: getPhoneLabel(number, type),
        value: dashToString(number)
      })),
      {
        key: demographicsPhones.length,
        label: getPhoneLabel(primaryPhone, 'Application'),
        value: dashToString(primaryPhone)
      },
      OTHER_DROPDOWN_VALUE
    ].filter(
      ({ value }, key, array) => value && findIndex(array, { value }) === key
    )
)

export const patientPhonesSelector = createSelector(
  patientDemographicsSelector,
  (demographics) =>
    demographics.phones.map((el) => ({
      ...el,
      number: dashToString(el.number)
    }))
)

/** The phones as the profile loaded them, without `patientPhonesSelector` formatting. */
export const patientRawPhonesSelector = createSelector(
  patientDemographicsSelector,
  (demographics) => demographics?.phones
)

export const patientAddressesSelector = createSelector(
  patientDemographicsSelector,
  (demographics) => demographics.addresses
)

export const patientAddressesDropdown$ = createSelector(
  patientAddressesSelector,
  (addresses = []) => {
    const mappedAddresses = addresses.map(
      ({ uuid, address, city, state, zip }) => ({
        key: uuid,
        value: uuid,
        title: `${address}, ${city}, ${state}, ${zip}`,
        address,
        state,
        city,
        zip
      })
    )

    mappedAddresses.push(OTHER_DROPDOWN_VALUE)

    return mappedAddresses
  }
)

export const patientEvents$ = createSelector(
  patientDemographicsSelector,
  (demographics) => demographics.events
)

export const assignedCaregivers$ = createSelector(
  patientDemographicsSelector,
  ({ assignedCaregivers = [] }) => assignedCaregivers
)

export const availableCaregivers$ = createSelector(
  patientDemographicsSelector,
  ({ availableCaregivers = [] }) => availableCaregivers
)

export const availableCaregiversDropdown$ = createSelector(
  patientUuidSelector,
  availableCaregivers$,
  (patientUuid, availableCaregivers = []) =>
    availableCaregivers
      .filter(({ enabled, assignedPatients = [] }) =>
        enabled ? true : findIndex(assignedPatients, { uuid: patientUuid }) > -1
      )
      .map(
        ({
          uuid = '',
          firstName = '',
          middleName = '',
          lastName = '',
          assignedPatients = [],
          isRpmBillableAutoAssign = false
        }) => ({
          key: uuid,
          value: uuid,
          title: [firstName, middleName, lastName].join(' '),
          disabled: findIndex(assignedPatients, { uuid: patientUuid }) > -1,
          isRpmBillableAutoAssign
        })
      )
)

export const patientAlertNote$ = createSelector(
  patientDemographicsSelector,
  ({ patientAlertNote }) => patientAlertNote
)

export const patientMergeList$ = createSelector(
  patientDemographicsSelector,
  patientUuidSelector,
  ({ patientMergeList = [] }, patientUuid = null) =>
    patientMergeList
      .map(({ fullName, patientId, uuid }) => ({
        key: uuid,
        value: uuid,
        title: `${fullName} (${patientId})`
      }))
      .filter(({ key }) => key !== patientUuid)
)
